import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useApiToken } from '../auth/AuthProvider';
import {
  listAdmins, addAdmin, removeAdmin, searchCgsUsers,
  listEvents, createEvent, updateEvent, deleteEvent, autoGenerateEvents,
  getAccountingDashboard, getAdminReceiptPreviewUrl,
  listAdminReports, assignReportToEvent,
  getLogs, getLogSettings, setLogSettings, getApiHealth, UI_VERSION,
  getAppSettings, updateAppSettings,
  AdminUser, EventData, AccountingDashboard, ReceiptDetail, AdminReport, LogEntry,
} from '../services/api';
import { useAppSettings } from '../services/AppSettingsContext';
import LoadingAnimation from '../components/LoadingAnimation';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import ConfirmModal from '../components/ConfirmModal';
import { ReceiptMetadata } from '../services/api';

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a365d', margin: '0 0 4px' },
  subtitle: { color: '#666', fontSize: 14, margin: '0 0 24px' },
  tabs: { display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e2e8f0' },
  tab: { padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#666', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#0078d4', borderBottom: '2px solid #0078d4', marginBottom: -2 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 18, fontWeight: 600, color: '#1a365d', margin: '0 0 12px' },
  card: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '10px 12px', background: '#f7fafc', borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#666' },
  td: { padding: '10px 12px', borderBottom: '1px solid #edf2f7' },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnDanger: { background: '#e53e3e', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  btnOutline: { background: 'white', color: '#0078d4', border: '1px solid #0078d4', padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  btnSmall: { background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 12 },
  input: { padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' as const },
  row: { display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-end' },
  label: { fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  statCard: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20 },
  statValue: { fontSize: 28, fontWeight: 700, color: '#1a365d', margin: 0 },
  statLabel: { fontSize: 13, color: '#666', margin: '4px 0 0' },
  searchResults: { background: 'white', border: '1px solid #cbd5e0', borderRadius: 4, maxHeight: 200, overflow: 'auto', position: 'absolute' as const, zIndex: 10, width: '100%' },
  searchItem: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0' },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' as const },
  filterLabel: { fontSize: 13, fontWeight: 600, color: '#333' },
  filterSelect: { padding: '6px 10px', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: 13, background: 'white' },
  filterInput: { padding: '6px 10px', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: 13, width: 140 },
};

const CATEGORY_COLORS: Record<string, string> = {
  'Meals': '#f6ad55',
  'Airfare': '#63b3ed',
  'Lodging': '#b794f4',
  'Transp.': '#68d391',
  'Mileage': '#4fd1c5',
  'Rental Car': '#7f9cf5',
  'Conference Registration': '#ecc94b',
  'Computer + Office Supplies': '#a0aec0',
  'Learning + Development': '#f6e05e',
  'Memberships + Subscriptions': '#f687b3',
  'Service Fees': '#b794f4',
  'Alcohol': '#fc8181',
  'Other': '#cbd5e0',
};

const USER_COLORS = ['#0078d4', '#38a169', '#d69e2e', '#e53e3e', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#3182ce', '#718096'];

function fmt(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

const TABS = ['Dashboard', 'Events', 'Admins', 'Logs', 'Settings'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function AccountingPage() {
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      <h1 style={styles.title}>Accounting</h1>
      <p style={styles.subtitle}>Admin dashboard for managing events, users, and expense overview</p>

      <div style={styles.tabs} data-tour="accounting-tabs">
        {TABS.map((tab, i) => (
          <div key={tab} style={i === activeTab ? styles.tabActive : styles.tab} onClick={() => setActiveTab(i)}>
            {tab}
          </div>
        ))}
      </div>

      {activeTab === 0 && <DashboardTab getToken={getToken} />}
      {activeTab === 1 && <EventsTab getToken={getToken} />}
      {activeTab === 2 && <AdminsTab getToken={getToken} />}
      {activeTab === 3 && <LogsTab getToken={getToken} />}
      {activeTab === 4 && <SettingsTab getToken={getToken} />}
    </div>
  );
}

// --- Stacked Bar Chart Component ---
interface BarChartProps {
  data: { label: string; segments: { key: string; value: number; color: string }[] }[];
  title: string;
  colorMap: Record<string, string>;
  onBarClick?: (label: string, segmentKey: string | undefined, ctrlKey: boolean) => void;
  activeSelections?: Set<string>; // "label" or "label|segment"
}

function StackedBarChart({ data, title, colorMap, onBarClick, activeSelections }: BarChartProps) {
  const maxTotal = Math.max(...data.map(d => d.segments.reduce((s, seg) => s + seg.value, 0)), 1);
  const allKeys = Array.from(new Set(data.flatMap(d => d.segments.map(s => s.key))));
  const hasSelection = activeSelections && activeSelections.size > 0;

  const isSegmentActive = (label: string, segKey: string) => {
    if (!hasSelection) return true;
    return activeSelections!.has(label) || activeSelections!.has(`${label}|${segKey}`);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a365d', margin: '0 0 12px' }}>{title}</h3>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {allKeys.map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colorMap[k] || '#cbd5e0', display: 'inline-block' }} />
            {k}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200, borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
        {data.map((d) => {
          const total = d.segments.reduce((s, seg) => s + seg.value, 0);
          return (
            <div
              key={d.label}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%', cursor: onBarClick ? 'pointer' : 'default' }}
              onClick={(e) => onBarClick?.(d.label, undefined, e.ctrlKey || e.metaKey)}
              title={`${d.label}: $${fmt(total)}`}
            >
              <div style={{ width: '80%', maxWidth: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                {d.segments.filter(s => s.value > 0).map((seg, i) => (
                  <div
                    key={i}
                    style={{
                      height: Math.max(2, (seg.value / maxTotal) * 180),
                      background: seg.color,
                      borderRadius: i === 0 ? '3px 3px 0 0' : 0,
                      opacity: isSegmentActive(d.label, seg.key) ? 1 : 0.15,
                      transition: 'opacity 0.2s',
                    }}
                    title={`${seg.key}: $${fmt(seg.value)}`}
                    onClick={(e) => { e.stopPropagation(); onBarClick?.(d.label, seg.key, e.ctrlKey || e.metaKey); }}
                  />
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#666', marginTop: 4, textAlign: 'center', lineHeight: 1.2, opacity: hasSelection && !activeSelections!.has(d.label) && !Array.from(activeSelections!).some(s => s.startsWith(d.label + '|')) ? 0.4 : 1 }}>{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Horizontal Stacked Bar Chart ---
function HorizontalStackedBarChart({ data, title, colorMap, onBarClick, activeSelections }: BarChartProps) {
  const maxTotal = Math.max(...data.map(d => d.segments.reduce((s, seg) => s + seg.value, 0)), 1);
  const allKeys = Array.from(new Set(data.flatMap(d => d.segments.map(s => s.key))));
  const hasSelection = activeSelections && activeSelections.size > 0;

  const isSegmentActive = (label: string, segKey: string) => {
    if (!hasSelection) return true;
    return activeSelections!.has(label) || activeSelections!.has(`${label}|${segKey}`);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a365d', margin: '0 0 12px' }}>{title}</h3>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {allKeys.map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: colorMap[k] || '#cbd5e0', display: 'inline-block' }} />
            {k}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.map((d) => {
          const total = d.segments.reduce((s, seg) => s + seg.value, 0);
          const barActive = !hasSelection || activeSelections!.has(d.label) || Array.from(activeSelections!).some(s => s.startsWith(d.label + '|'));
          return (
            <div
              key={d.label}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: onBarClick ? 'pointer' : 'default', transition: 'opacity 0.2s' }}
              onClick={(e) => onBarClick?.(d.label, undefined, e.ctrlKey || e.metaKey)}
              title={`${d.label}: $${fmt(total)}`}
            >
              <div style={{ width: 120, fontSize: 12, color: '#333', fontWeight: 500, textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: barActive ? 1 : 0.3 }}>{d.label}</div>
              <div style={{ flex: 1, display: 'flex', height: 28, borderRadius: 4, overflow: 'hidden', background: '#f7fafc' }}>
                {d.segments.filter(s => s.value > 0).map((seg, i) => (
                  <div
                    key={i}
                    style={{
                      width: `${(seg.value / maxTotal) * 100}%`,
                      minWidth: 2,
                      background: seg.color,
                      opacity: isSegmentActive(d.label, seg.key) ? 1 : 0.15,
                      transition: 'opacity 0.2s',
                    }}
                    title={`${seg.key}: $${fmt(seg.value)}`}
                    onClick={(e) => { e.stopPropagation(); onBarClick?.(d.label, seg.key, e.ctrlKey || e.metaKey); }}
                  />
                ))}
              </div>
              <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: '#333', textAlign: 'right', flexShrink: 0, opacity: barActive ? 1 : 0.3 }}>${fmt(total)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Dashboard Tab ---
function DashboardTab({ getToken }: { getToken: () => Promise<string> }) {
  const [data, setData] = useState<AccountingDashboard | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  // Multi-select chart filtering: { chart: 'month'|'user'|'event', selections: Set<'label' or 'label|segment'> }
  const [chartFilter, setChartFilter] = useState<{ chart: string; selections: Set<string> } | null>(null);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptDetail | null>(null);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const filters: any = {};
      if (dateStart && dateEnd) {
        filters.date_start = dateStart;
        filters.date_end = dateEnd;
      } else {
        filters.year = year;
      }
      if (eventFilter) filters.event_id = eventFilter;
      if (userFilter) filters.user_filter = userFilter;
      setData(await getAccountingDashboard(token, filters));
    } catch (e) {
      console.error('Dashboard load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [getToken, year, dateStart, dateEnd, eventFilter, userFilter]);

  // Auto-load whenever any filter changes
  const filterKey = `${year}-${dateStart}-${dateEnd}-${eventFilter}-${userFilter}`;
  useEffect(() => {
    setChartFilter(null);
    loadDashboard();
  }, [filterKey, loadDashboard]);

  const handleYearChange = (yr: number) => {
    setYear(yr);
    setDateStart('');
    setDateEnd('');
    setChartFilter(null);
  };

  // Build chart data
  const userColorMap = useMemo(() => {
    if (!data) return {};
    const map: Record<string, string> = {};
    Object.keys(data.receipts_by_user).forEach((name, i) => {
      map[name] = USER_COLORS[i % USER_COLORS.length];
    });
    return map;
  }, [data]);

  const monthChartData = useMemo(() => {
    if (!data) return [];
    const yr = data.year;
    return MONTHS.map((name, i) => {
      const key = `${yr}-${String(i + 1).padStart(2, '0')}`;
      const monthData = data.receipts_by_month[key];
      const segments = monthData
        ? Object.entries(monthData.by_user).map(([user, amount]) => ({
            key: user,
            value: amount,
            color: userColorMap[user] || '#cbd5e0',
          }))
        : [];
      return { label: name, segments };
    });
  }, [data, userColorMap]);

  const userChartData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.receipts_by_user).map(([name, info]) => ({
      label: name,
      segments: Object.entries(info.by_category).map(([cat, amount]) => ({
        key: cat,
        value: amount,
        color: CATEGORY_COLORS[cat] || '#cbd5e0',
      })),
    }));
  }, [data]);

  const eventChartData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.receipts_by_event).map(([name, info]) => ({
      label: name.length > 15 ? name.slice(0, 15) + '...' : name,
      segments: Object.entries(info.by_category).map(([cat, amount]) => ({
        key: cat,
        value: amount,
        color: CATEGORY_COLORS[cat] || '#cbd5e0',
      })),
    }));
  }, [data]);

  // Filtered receipt details based on chart clicks
  const filteredDetails = useMemo(() => {
    if (!data) return [];
    let details = data.receipt_details;
    if (chartFilter && chartFilter.selections.size > 0) {
      const { chart, selections } = chartFilter;
      details = details.filter(r => {
        for (const sel of Array.from(selections)) {
          const [label, segment] = sel.includes('|') ? sel.split('|', 2) : [sel, undefined];
          if (chart === 'month') {
            const monthIdx = MONTHS.indexOf(label);
            if (monthIdx >= 0) {
              const prefix = `${data.year}-${String(monthIdx + 1).padStart(2, '0')}`;
              if (r.receipt_date.startsWith(prefix)) {
                if (!segment || r.user_name === segment) return true;
              }
            }
          } else if (chart === 'user') {
            if (r.user_name === label) {
              if (!segment || r.category === segment) return true;
            }
          } else if (chart === 'event') {
            const fullName = Object.keys(data.receipts_by_event).find(n =>
              (n.length > 15 ? n.slice(0, 15) + '...' : n) === label
            ) || label;
            if ((r.event_name || 'Unassigned') === fullName) {
              if (!segment || r.category === segment) return true;
            }
          }
        }
        return false;
      });
    }
    return details;
  }, [data, chartFilter]);

  const handleBarClick = (chart: string) => (label: string, segmentKey: string | undefined, ctrlKey: boolean) => {
    const key = segmentKey ? `${label}|${segmentKey}` : label;
    setChartFilter(prev => {
      if (ctrlKey && prev?.chart === chart) {
        // Ctrl+click: toggle this selection within the same chart
        const next = new Set(prev.selections);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next.size === 0 ? null : { chart, selections: next };
      }
      // Normal click: single selection (or deselect if already selected)
      if (prev?.chart === chart && prev.selections.size === 1 && prev.selections.has(key)) {
        return null;
      }
      return { chart, selections: new Set([key]) };
    });
  };

  const exportExcel = () => {
    if (filteredDetails.length === 0) return;
    const headers = ['Date', 'Vendor', 'Category', 'Amount', 'User', 'Event', 'Status', 'File'];
    const rows = filteredDetails.map(r => [
      r.receipt_date, r.vendor_name, r.category, r.receipt_amount.toFixed(2),
      r.user_name, r.event_name || '', r.status, r.file_name,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense_receipts_${data?.year || 'export'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !data) return <LoadingAnimation />;
  if (!data) return <p>Failed to load dashboard.</p>;

  const yearsAvailable = data.years_with_data.length > 0 ? data.years_with_data : [new Date().getFullYear()];

  return (
    <div>
      {/* Filters */}
      <div style={styles.filterRow}>
        <span style={styles.filterLabel}>Year:</span>
        <select style={styles.filterSelect} value={year} onChange={(e) => handleYearChange(Number(e.target.value))}>
          {yearsAvailable.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <span style={styles.filterLabel}>From:</span>
        <input type="date" style={styles.filterInput} value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
        <span style={styles.filterLabel}>To:</span>
        <input type="date" style={styles.filterInput} value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />

        {data.events.length > 0 && (
          <>
            <span style={styles.filterLabel}>Event:</span>
            <select style={styles.filterSelect} value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}>
              <option value="">All Events</option>
              {data.events.map(ev => <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>)}
            </select>
          </>
        )}

        {Object.keys(data.user_names).length > 1 && (
          <>
            <span style={styles.filterLabel}>User:</span>
            <select style={styles.filterSelect} value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
              <option value="">All Users</option>
              {Object.entries(data.user_names).map(([oid, name]) => (
                <option key={oid} value={oid}>{name}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Stats */}
      <div style={styles.statGrid}>
        <div style={styles.statCard}>
          <p style={styles.statValue}>{fmtInt(data.total_receipts)}</p>
          <p style={styles.statLabel}>Total Receipts</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statValue}>{fmtInt(data.total_reports)}</p>
          <p style={styles.statLabel}>Total Reports</p>
        </div>
        <div style={styles.statCard}>
          <p style={{ ...styles.statValue, color: '#38a169' }}>${fmt(data.total_amount)}</p>
          <p style={styles.statLabel}>Total Amount</p>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
        <StackedBarChart
          data={monthChartData}
          title="Spending by Month & User"
          colorMap={userColorMap}
          onBarClick={handleBarClick('month')}
          activeSelections={chartFilter?.chart === 'month' ? chartFilter.selections : undefined}
        />
        <HorizontalStackedBarChart
          data={userChartData}
          title="By User & Expense Type"
          colorMap={CATEGORY_COLORS}
          onBarClick={handleBarClick('user')}
          activeSelections={chartFilter?.chart === 'user' ? chartFilter.selections : undefined}
        />
        <HorizontalStackedBarChart
          data={eventChartData}
          title="By Event & Expense Type"
          colorMap={CATEGORY_COLORS}
          onBarClick={handleBarClick('event')}
          activeSelections={chartFilter?.chart === 'event' ? chartFilter.selections : undefined}
        />
      </div>

      {/* Receipt Details Table */}
      <div style={{ ...styles.section, marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={styles.sectionTitle}>
            Receipt Details ({fmtInt(filteredDetails.length)})
            {chartFilter && chartFilter.selections.size > 0 && (
              <button
                style={{ ...styles.btnSmall, color: '#0078d4', marginLeft: 12, fontSize: 12 }}
                onClick={() => setChartFilter(null)}
              >
                Clear filter ({chartFilter.selections.size} selected)
              </button>
            )}
          </h2>
          <button style={styles.btnOutline} onClick={exportExcel} disabled={filteredDetails.length === 0}>
            Export CSV
          </button>
        </div>
        <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1, width: 40 }}></th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Date</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Vendor</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Category</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1, textAlign: 'right' as const }}>Amount</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>User</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Event</th>
              </tr>
            </thead>
            <tbody>
              {filteredDetails.length === 0 ? (
                <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No receipts match current filters</td></tr>
              ) : (
                filteredDetails.map((r) => (
                  <tr key={r.receipt_id}>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {r.blob_path && (
                        <span
                          style={{ color: '#0078d4', fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={async () => {
                            try {
                              const token = await getToken();
                              setPreviewReceiptUrl(getAdminReceiptPreviewUrl(token, r.receipt_id));
                              setPreviewReceipt(r);
                            } catch {}
                          }}
                        >View</span>
                      )}
                    </td>
                    <td style={styles.td}>{r.receipt_date}</td>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{r.vendor_name}</td>
                    <td style={styles.td}>
                      <span style={{
                        display: 'inline-block',
                        background: CATEGORY_COLORS[r.category] || '#e2e8f0',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 3,
                      }}>
                        {r.category}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>${fmt(r.receipt_amount)}</td>
                    <td style={styles.td}>{r.user_name}</td>
                    <td style={styles.td}>{r.event_name || <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredDetails.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f7fafc' }}>
                  <td colSpan={4} style={{ ...styles.td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                    ${fmt(filteredDetails.reduce((s, r) => s + r.receipt_amount, 0))}
                  </td>
                  <td colSpan={2} style={styles.td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Receipt preview modal — shares component with Line Items for consistency. */}
      <ReceiptPreviewModal
        receipt={previewReceipt ? receiptDetailToMetadata(previewReceipt) : null}
        previewUrl={previewReceiptUrl}
        readOnly
        onClose={() => { setPreviewReceipt(null); setPreviewReceiptUrl(null); }}
      />
    </div>
  );
}

/** Adapt an admin ReceiptDetail into the ReceiptMetadata shape the preview modal expects. */
function receiptDetailToMetadata(r: ReceiptDetail): ReceiptMetadata {
  return {
    receipt_id: r.receipt_id,
    file_name: r.file_name,
    upload_timestamp: '',
    source: '',
    blob_path: r.blob_path || '',
    file_size_bytes: 0,
    download_url: r.download_url || '',
    receipt_date: r.receipt_date,
    vendor_name: r.vendor_name,
    receipt_amount: r.receipt_amount,
    status: r.status,
  };
}

// --- Events Tab ---
function EventsTab({ getToken }: { getToken: () => Promise<string> }) {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);
  const [newName, setNewName] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventData, setEventData] = useState<AccountingDashboard | null>(null);
  const [eventDataLoading, setEventDataLoading] = useState(false);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [assigningReportId, setAssigningReportId] = useState<string | null>(null);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        const [evts, rpts] = await Promise.all([listEvents(token), listAdminReports(token)]);
        setEvents(evts);
        setReports(rpts);
      } catch (e) {
        console.error('Failed to load events/reports:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const handleCreate = async () => {
    if (!newName || !newStart || !newEnd) return;
    try {
      const token = await getToken();
      const event = await createEvent(token, { name: newName, start_date: newStart, end_date: newEnd });
      setEvents([event, ...events]);
      setNewName('');
      setNewStart('');
      setNewEnd('');
      setShowCreate(false);
    } catch (e) {
      alert(`Failed to create event: ${e}`);
    }
  };

  const handleDelete = (eventId: string) => {
    setDeleteEventId(eventId);
  };

  const executeDelete = async () => {
    if (!deleteEventId) return;
    try {
      const token = await getToken();
      await deleteEvent(token, deleteEventId);
      setEvents(events.filter((e) => e.event_id !== deleteEventId));
    } catch (e) {
      alert(`Failed to delete: ${e}`);
    }
    setDeleteEventId(null);
  };

  const showToast = (message: string, type: 'success' | 'info' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleAutoGenerate = async () => {
    setGenerating(true);
    try {
      const token = await getToken();
      const result = await autoGenerateEvents(token);
      if (result.events_created > 0) {
        // Refresh events and reports to pick up auto-assignments
        const [evts, rpts] = await Promise.all([listEvents(token), listAdminReports(token)]);
        setEvents(evts);
        setReports(rpts);
        showToast(`Created ${result.events_created} new event(s) and auto-assigned matching reports`, 'success');
      } else {
        showToast('No new events to create — all existing reports are already covered', 'info');
      }
    } catch (e) {
      showToast(`Auto-generate failed: ${e}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectEvent = async (event: EventData) => {
    if (selectedEventId === event.event_id) {
      setSelectedEventId(null);
      setEventData(null);
      return;
    }
    setSelectedEventId(event.event_id);
    setEventDataLoading(true);
    try {
      const token = await getToken();
      // Only pass event_id — the backend fetches receipts via report
      // assignment so all receipts in reports assigned to this event
      // are included regardless of their individual receipt dates.
      const data = await getAccountingDashboard(token, {
        event_id: event.event_id,
      });
      setEventData(data);
    } catch (e) {
      console.error('Failed to load event data:', e);
    } finally {
      setEventDataLoading(false);
    }
  };

  const startEdit = (event: EventData) => {
    setEditingId(event.event_id);
    setEditName(event.name);
    setEditStart(event.start_date);
    setEditEnd(event.end_date);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    try {
      const token = await getToken();
      const updated = await updateEvent(token, editingId, {
        name: editName,
        start_date: editStart,
        end_date: editEnd,
      });
      setEvents(events.map((e) => (e.event_id === editingId ? updated : e)));
      setEditingId(null);
    } catch (e) {
      alert(`Failed to update: ${e}`);
    }
  };

  const handleAssignReport = async (reportId: string, eventId: string) => {
    try {
      const token = await getToken();
      await assignReportToEvent(token, reportId, eventId);
      setReports(reports.map(r => r.report_id === reportId ? { ...r, event_id: eventId } : r));
      setAssigningReportId(null);
      // Refresh events to update report counts
      setEvents(await listEvents(token));
    } catch (e) {
      alert(`Failed to assign: ${e}`);
    }
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div>
      {/* Toast notification */}
      <style>{`@keyframes toastSlideDown { from { opacity: 0; transform: translateX(-50%) translateY(-20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 2000, minWidth: 350, maxWidth: 600, padding: '14px 24px',
          borderRadius: 8, fontSize: 14, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', gap: 10,
          background: toast.type === 'success' ? '#f0fff4' : toast.type === 'error' ? '#fff5f5' : '#ebf8ff',
          border: `1px solid ${toast.type === 'success' ? '#38a169' : toast.type === 'error' ? '#e53e3e' : '#3182ce'}`,
          color: toast.type === 'success' ? '#22543d' : toast.type === 'error' ? '#742a2a' : '#2a4365',
          animation: 'toastSlideDown 0.3s ease-out',
        }}>
          <span style={{ fontSize: 18 }}>{toast.type === 'success' ? '✓' : toast.type === 'error' ? '✗' : 'ℹ'}</span>
          <span style={{ flex: 1 }}>{toast.message}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'inherit', padding: '0 4px', lineHeight: 1, opacity: 0.6 }}>&times;</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button style={styles.btn} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : 'Create Event'}
        </button>
        <button style={styles.btnOutline} onClick={handleAutoGenerate} disabled={generating}>
          {generating ? 'Analyzing reports...' : 'Auto-Generate from Reports'}
        </button>
      </div>

      {showCreate && (
        <div style={styles.card}>
          <div style={styles.row}>
            <div style={{ flex: 2 }}>
              <label style={styles.label}>Event Name</label>
              <input style={styles.input} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Esri Developer Summit" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Start Date</label>
              <input style={styles.input} type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>End Date</label>
              <input style={styles.input} type="date" value={newEnd} min={newStart || undefined} onChange={(e) => setNewEnd(e.target.value)} />
            </div>
            <button style={styles.btn} onClick={handleCreate}>Create</button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <p style={{ color: '#999', fontSize: 14, fontStyle: 'italic' }}>No events yet. Create one or auto-generate from existing reports.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Event Name</th>
              <th style={styles.th}>Start Date</th>
              <th style={styles.th}>End Date</th>
              <th style={styles.th}>Reports</th>
              <th style={{ ...styles.th, textAlign: 'right' as const }}>Total</th>
              <th style={styles.th}>Source</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <React.Fragment key={e.event_id}>
              <tr>
                {editingId === e.event_id ? (
                  <>
                    <td style={styles.td}>
                      <input style={{ ...styles.input, width: '100%' }} value={editName} onChange={(ev) => setEditName(ev.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input style={styles.input} type="date" value={editStart} onChange={(ev) => setEditStart(ev.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input style={styles.input} type="date" value={editEnd} min={editStart || undefined} onChange={(ev) => setEditEnd(ev.target.value)} />
                    </td>
                    <td style={styles.td}>{e.report_count}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>${fmt(e.total_amount)}</td>
                    <td style={styles.td}>{e.auto_generated ? 'Auto' : 'Manual'}</td>
                    <td style={styles.td}>
                      <button style={styles.btn} onClick={handleSaveEdit}>Save</button>{' '}
                      <button style={styles.btnSmall} onClick={() => setEditingId(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td
                      style={{ ...styles.td, fontWeight: 600, color: '#0078d4', cursor: 'pointer' }}
                      onClick={() => handleSelectEvent(e)}
                    >
                      {selectedEventId === e.event_id ? '\u25BC ' : '\u25B6 '}{e.name}
                    </td>
                    <td style={styles.td}>{e.start_date}</td>
                    <td style={styles.td}>{e.end_date}</td>
                    <td style={styles.td}>{e.report_count}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: '#38a169' }}>${fmt(e.total_amount)}</td>
                    <td style={styles.td}>
                      <span style={{
                        background: e.auto_generated ? '#ebf8ff' : '#f0fff4',
                        color: e.auto_generated ? '#2b6cb0' : '#22543d',
                        fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                      }}>
                        {e.auto_generated ? 'Auto-generated' : 'Manual'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button style={{ ...styles.btnSmall, color: '#0078d4' }} onClick={() => startEdit(e)}>Edit</button>{' '}
                      <button style={styles.btnSmall} onClick={() => handleDelete(e.event_id)}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
              {/* Event detail rows */}
              {selectedEventId === e.event_id && (
                <tr>
                  <td colSpan={7} style={{ padding: '16px 12px', background: '#f7fafc' }}>
                    {eventDataLoading ? (
                      <LoadingAnimation />
                    ) : eventData ? (
                      <div>
                        {/* Table 1: Totals by user */}
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1a365d', margin: '0 0 8px' }}>
                          Totals by User ({fmtInt(eventData.total_receipts)} receipts, ${fmt(eventData.total_amount)})
                        </h4>
                        <table style={{ ...styles.table, marginBottom: 16 }}>
                          <thead>
                            <tr>
                              <th style={styles.th}>User</th>
                              <th style={styles.th}>Receipts</th>
                              <th style={{ ...styles.th, textAlign: 'right' as const }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(eventData.receipts_by_user).map(([name, info]) => (
                              <tr key={name}>
                                <td style={{ ...styles.td, fontWeight: 500 }}>{name}</td>
                                <td style={styles.td}>{fmtInt(info.count)}</td>
                                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>${fmt(info.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Table 2: Detailed receipts */}
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1a365d', margin: '0 0 8px' }}>Receipt Details</h4>
                        <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Date</th>
                                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Vendor</th>
                                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Category</th>
                                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1, textAlign: 'right' as const }}>Amount</th>
                                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>User</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eventData.receipt_details.map(r => (
                                <tr key={r.receipt_id}>
                                  <td style={styles.td}>{r.receipt_date}</td>
                                  <td style={{ ...styles.td, fontWeight: 500 }}>{r.vendor_name}</td>
                                  <td style={styles.td}>
                                    <span style={{
                                      display: 'inline-block',
                                      background: CATEGORY_COLORS[r.category] || '#e2e8f0',
                                      color: '#fff',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: 3,
                                    }}>{r.category}</span>
                                  </td>
                                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>${fmt(r.receipt_amount)}</td>
                                  <td style={styles.td}>{r.user_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: '#999', fontSize: 13 }}>No data available</p>
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* Reports Section */}
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a365d', margin: '32px 0 12px' }}>Reports</h3>
      {reports.length === 0 ? (
        <p style={{ color: '#999', fontSize: 14, fontStyle: 'italic' }}>No reports yet.</p>
      ) : (
        <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Title</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Employee</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Date Range</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1, textAlign: 'right' as const }}>Amount</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Receipts</th>
                <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Event</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const eventName = events.find(e => e.event_id === r.event_id)?.name || '';
                return (
                  <tr key={r.report_id}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{r.title || 'Untitled'}</td>
                    <td style={styles.td}>{r.employee_name || r.user_name}</td>
                    <td style={styles.td}>{r.date_range_start} to {r.date_range_end}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>${fmt(r.total_amount)}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{r.receipt_count}</td>
                    <td style={styles.td}>
                      {assigningReportId === r.report_id ? (
                        <select
                          autoFocus
                          style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '4px 6px' }}
                          value={r.event_id || ''}
                          onChange={(e) => handleAssignReport(r.report_id, e.target.value)}
                          onBlur={() => setAssigningReportId(null)}
                        >
                          <option value="">-- None --</option>
                          {events.map(ev => (
                            <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span
                          style={{ cursor: 'pointer', color: eventName ? '#1a365d' : '#0078d4', fontWeight: eventName ? 500 : 400 }}
                          onClick={() => setAssigningReportId(r.report_id)}
                          title="Click to assign to an event"
                        >
                          {eventName || '+ Assign'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={deleteEventId !== null}
        title="Delete event?"
        message="This will remove the event and unassign its reports. This can't be undone."
        confirmLabel="Delete"
        danger
        onConfirm={executeDelete}
        onCancel={() => setDeleteEventId(null)}
      />
    </div>
  );
}

// --- Admins Tab ---
function AdminsTab({ getToken }: { getToken: () => Promise<string> }) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ email: string; name: string }[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        setAdmins(await listAdmins(token));
      } catch (e) {
        console.error('Failed to load admins:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const token = await getToken();
        const results = await searchCgsUsers(token, query);
        setSearchResults(results);
        setShowSearch(results.length > 0);
      } catch {
        setSearchResults([]);
      }
    }, 300);
  };

  const handleAddAdmin = async (email: string, name: string) => {
    try {
      const token = await getToken();
      const admin = await addAdmin(token, email, name);
      setAdmins([...admins, admin]);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
    } catch (e) {
      alert(`Failed to add admin: ${e}`);
    }
  };

  const handleRemove = async (email: string) => {
    if (!window.confirm(`Remove ${email} as admin?`)) return;
    try {
      const token = await getToken();
      await removeAdmin(token, email);
      setAdmins(admins.filter((a) => a.email.toLowerCase() !== email.toLowerCase()));
    } catch (e) {
      alert(`Failed to remove: ${e}`);
    }
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div>
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Add Admin</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
          Search for CGS accounts to add as admins. Only @cultivategeospatial.com emails are allowed.
        </p>
        <div style={{ position: 'relative', maxWidth: 400 }}>
          <input
            style={styles.input}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name or email..."
          />
          {showSearch && (
            <div style={styles.searchResults}>
              {searchResults.map((u) => {
                const isAlready = admins.some((a) => a.email.toLowerCase() === u.email.toLowerCase());
                return (
                  <div
                    key={u.email}
                    style={{
                      ...styles.searchItem,
                      ...(isAlready ? { opacity: 0.5, cursor: 'default' } : {}),
                    }}
                    onClick={() => !isAlready && handleAddAdmin(u.email, u.name)}
                  >
                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>{u.email}</div>
                    {isAlready && <span style={{ fontSize: 10, color: '#38a169' }}>Already admin</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Current Admins ({admins.length})</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Added By</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.email}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{a.email}</td>
                <td style={styles.td}>{a.name || '-'}</td>
                <td style={styles.td}>{a.added_by || 'system'}</td>
                <td style={styles.td}>
                  {a.added_by === 'system' ? (
                    <span style={{ fontSize: 11, color: '#999' }}>Seed admin</span>
                  ) : (
                    <button style={styles.btnSmall} onClick={() => handleRemove(a.email)}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// --- Logs Tab ---
function LogsTab({ getToken }: { getToken: () => Promise<string> }) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [captureLevel, setCaptureLevel] = useState('INFO');
  const [showLevel, setShowLevel] = useState('ALL');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [apiVersion, setApiVersion] = useState<string>('...');

  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const toDateStr = (d: Date) => d.toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    date_start: toDateStr(weekAgo),
    date_end: toDateStr(today),
    log_user: '',
    module: '',
    function_name: '',
    search: '',
    limit: 200,
  });

  const loadedRef = useRef(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params: any = { ...filters };
      if (!params.log_user) delete params.log_user;
      if (!params.module) delete params.module;
      if (!params.function_name) delete params.function_name;
      if (!params.search) delete params.search;
      const result = await getLogs(token, params);
      setLogs(result);
      setSelectedIndex(null);
    } catch (e) {
      console.error('Failed to load logs:', e);
    } finally {
      setLoading(false);
    }
  }, [getToken, filters]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const [, health] = await Promise.all([
          (async () => {
            const token = await getToken();
            const settings = await getLogSettings(token);
            setCaptureLevel(settings.capture_level);
          })(),
          getApiHealth().catch(() => ({ status: 'error', version: '?' })),
        ]);
        setApiVersion(health.version || '?');
      } catch (e) {
        console.error('Failed to load log settings:', e);
      }
      fetchLogs();
    })();
  }, [getToken, fetchLogs]);

  const handleSaveSettings = async () => {
    try {
      const token = await getToken();
      const result = await setLogSettings(token, captureLevel);
      setCaptureLevel(result.capture_level);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (e) {
      alert(`Failed to save settings: ${e}`);
    }
  };

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${mm}/${dd} ${hh}:${min}:${ss}`;
    } catch {
      return ts;
    }
  };

  const filteredLogs = showLevel === 'ALL' ? logs : logs.filter(l => l.level === showLevel);

  const levelBadge = (level: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 600,
    color: level === 'DEBUG' ? '#4a5568' : '#2b6cb0',
    background: level === 'DEBUG' ? '#e2e8f0' : '#bee3f8',
  });

  return (
    <div>
      {/* Version Info */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, padding: '8px 12px', background: '#edf2f7', borderRadius: 6, fontSize: 12, color: '#4a5568', fontFamily: 'monospace' }}>
        <span>UI: <strong>{UI_VERSION}</strong></span>
        <span>API: <strong>{apiVersion}</strong></span>
      </div>

      {/* Controls Row 1 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' as const }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Capture Level</label>
          <select
            style={styles.filterSelect}
            value={captureLevel}
            onChange={(e) => setCaptureLevel(e.target.value)}
          >
            <option value="DEBUG">DEBUG</option>
            <option value="INFO">INFO</option>
          </select>
          <button style={styles.btn} onClick={handleSaveSettings}>Save</button>
          {settingsSaved && <span style={{ fontSize: 12, color: '#38a169', fontWeight: 600 }}>Saved</span>}
        </div>

        <div style={{ width: 1, height: 24, background: '#e2e8f0' }} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Show Level</label>
          <select
            style={styles.filterSelect}
            value={showLevel}
            onChange={(e) => setShowLevel(e.target.value)}
          >
            <option value="ALL">ALL</option>
            <option value="DEBUG">DEBUG</option>
            <option value="INFO">INFO</option>
          </select>
        </div>
      </div>

      {/* Controls Row 2 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' as const }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Start</label>
          <input
            style={styles.filterInput}
            type="date"
            value={filters.date_start}
            onChange={(e) => setFilters({ ...filters, date_start: e.target.value })}
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>End</label>
          <input
            style={styles.filterInput}
            type="date"
            value={filters.date_end}
            onChange={(e) => setFilters({ ...filters, date_end: e.target.value })}
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>User</label>
          <input
            style={styles.filterInput}
            value={filters.log_user}
            onChange={(e) => setFilters({ ...filters, log_user: e.target.value })}
            placeholder="Filter user..."
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Module</label>
          <input
            style={styles.filterInput}
            value={filters.module}
            onChange={(e) => setFilters({ ...filters, module: e.target.value })}
            placeholder="Filter module..."
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Search</label>
          <input
            style={styles.filterInput}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search messages..."
          />
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <label style={styles.filterLabel}>Limit</label>
          <select
            style={styles.filterSelect}
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
        </div>
        <button style={styles.btn} onClick={fetchLogs}>Refresh</button>
      </div>

      {/* Logs Table + Detail Pane */}
      {loading ? (
        <LoadingAnimation />
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
          <div style={{ flex: '1 1 60%', minWidth: 0, maxHeight: 600, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Time</th>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Level</th>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>User</th>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Module</th>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Function</th>
                  <th style={{ ...styles.th, position: 'sticky' as const, top: 0, zIndex: 1 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, textAlign: 'center' as const, color: '#999', fontStyle: 'italic' }}>
                      No log entries found.
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log, idx) => {
                    const isSelected = selectedIndex === idx;
                    return (
                      <tr
                        key={idx}
                        onClick={() => setSelectedIndex(isSelected ? null : idx)}
                        style={{
                          background: isSelected ? '#e6f2ff' : idx % 2 === 0 ? '#fff' : '#f8f9fa',
                          verticalAlign: 'top',
                          cursor: 'pointer',
                          borderLeft: isSelected ? '3px solid #0078d4' : '3px solid transparent',
                        }}
                      >
                        <td style={{ ...styles.td, whiteSpace: 'nowrap' as const, fontFamily: 'monospace', fontSize: 12 }}>
                          {formatTime(log.timestamp)}
                        </td>
                        <td style={styles.td}>
                          <span style={levelBadge(log.level)}>{log.level}</span>
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{log.user}</td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{log.module}</td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{log.function_name}</td>
                        <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                          {log.message}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <LogDetailPane log={selectedIndex !== null ? filteredLogs[selectedIndex] : null} levelBadge={levelBadge} />
        </div>
      )}
    </div>
  );
}

// --- Settings Tab ---
function SettingsTab({ getToken }: { getToken: () => Promise<string> }) {
  const { mileageRate: contextRate, refreshSettings } = useAppSettings();
  const [rateInput, setRateInput] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const loadedRef = React.useRef(false);

  React.useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        const s = await getAppSettings(token);
        setRateInput(s.mileage_rate.toString());
      } catch {
        setRateInput(contextRate.toString());
      }
    })();
  }, [getToken, contextRate]);

  const handleSave = async () => {
    const rate = parseFloat(rateInput);
    if (isNaN(rate) || rate <= 0) {
      setError('Enter a valid rate greater than 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      await updateAppSettings(token, { mileage_rate: rate });
      await refreshSettings();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(`Failed to save: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Mileage Rate</h2>
        <div style={styles.card}>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>
            Reimbursement rate per mile. Changes apply to all new reports.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={styles.label}>Rate ($/mile)</label>
            <input
              style={{ ...styles.input, width: 120 }}
              type="number"
              step="0.001"
              min="0"
              value={rateInput}
              onChange={(e) => { setRateInput(e.target.value); setSaved(false); setError(''); }}
            />
            <button style={styles.btn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            {saved && <span style={{ fontSize: 13, color: '#38a169', fontWeight: 600 }}>Saved</span>}
            {error && <span style={{ fontSize: 13, color: '#e53e3e' }}>{error}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LogDetailPane({ log, levelBadge }: { log: LogEntry | null; levelBadge: (level: string) => React.CSSProperties }) {
  const prettyMetadata = React.useMemo(() => {
    if (!log || !log.metadata || log.metadata === '{}' || log.metadata === 'null') return null;
    try {
      return JSON.stringify(JSON.parse(log.metadata), null, 2);
    } catch {
      return log.metadata;
    }
  }, [log]);

  const fmtTimestamp = (ts: string) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const paneStyle: React.CSSProperties = {
    flex: '1 1 40%',
    minWidth: 320,
    maxHeight: 600,
    overflow: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 16,
    background: '#fff',
  };

  if (!log) {
    return (
      <div style={{ ...paneStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', fontSize: 13, fontStyle: 'italic' }}>
        Select a log entry to see details
      </div>
    );
  }

  const fieldLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 };
  const fieldValue: React.CSSProperties = { fontSize: 13, color: '#1a365d', marginBottom: 10, fontFamily: 'monospace', wordBreak: 'break-word' };

  return (
    <div style={paneStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={levelBadge(log.level)}>{log.level}</span>
        <span style={{ fontSize: 12, color: '#718096', fontFamily: 'monospace' }}>{fmtTimestamp(log.timestamp)}</span>
      </div>

      <div style={fieldLabel}>Message</div>
      <div style={{ ...fieldValue, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{log.message}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <div style={fieldLabel}>User</div>
          <div style={fieldValue}>{log.user || '--'}</div>
        </div>
        <div>
          <div style={fieldLabel}>Module</div>
          <div style={fieldValue}>{log.module || '--'}</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={fieldLabel}>Function</div>
          <div style={fieldValue}>{log.function_name || '--'}</div>
        </div>
      </div>

      {prettyMetadata && (
        <>
          <div style={{ ...fieldLabel, marginTop: 8 }}>Metadata</div>
          <pre style={{
            background: '#1a365d',
            color: '#e2e8f0',
            padding: 10,
            borderRadius: 4,
            fontSize: 11,
            fontFamily: 'monospace',
            overflow: 'auto',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>{prettyMetadata}</pre>
        </>
      )}
    </div>
  );
}
